import {
  ClockCircleOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  RightOutlined,
  RiseOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
// Branded hero banners: LAMF-1 on desktop (side column), LAMF-2 on mobile (header band).
import lamfDesktop from '~/assets/LAMF-1.jpg';
import lamfMobile from '~/assets/LAMF-2.jpg';
// Ant Design has no banknote glyph, so use an SVG asset to match the design.
import banknote from '~/assets/banknote.svg';

interface Feature {
  icon: ReactNode;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: <ClockCircleOutlined />,
    title: 'Loan Under 15 Mins',
    description: 'Funds credited to your bank account in just 15 minutes',
  },
  {
    icon: <FileTextOutlined />,
    title: 'Zero Paperwork',
    description: 'Fully paperless, only your Aadhaar and PAN required',
  },
  {
    icon: <CreditCardOutlined />,
    title: 'Maximum Loan Amount',
    description: 'Get loan up to 70% on Equity & 70% on Debt MFs',
  },
  {
    icon: <RiseOutlined />,
    title: 'Best Interest Rate',
    description: 'Competitive rate as low as 9.99% p.a. with SIB',
  },
  {
    icon: <SafetyOutlined />,
    title: 'Retain Ownership',
    description:
      'Your mutual fund holdings remain intact even while you borrow',
  },
  {
    icon: <img src={banknote} alt="" className="w-4 h-4" />,
    title: 'Flexible Repayment',
    description: 'Choose from flexible options, pay only for what you use',
  },
];

interface SibLandingProps {
  onGetLoan: () => void;
  loading: boolean;
}

const FeatureCard = ({ icon, title, description }: Feature) => (
  <div className="rounded-xl border border-gray-100 p-4 flex flex-col gap-2 sm:flex-row sm:gap-3 sm:items-start">
    <span className="w-9 h-9 shrink-0 rounded-lg bg-[#FCE7EA] text-[#C41230] flex items-center justify-center text-base">
      {icon}
    </span>
    <div>
      <p className="font-semibold text-gray-900 text-xs">{title}</p>
      <p className="text-gray-500 text-[10px] mt-1 leading-relaxed">
        {description}
      </p>
    </div>
  </div>
);

const SibLanding = ({ onGetLoan, loading }: SibLandingProps) => {
  return (
    <div className="min-h-[100vh] bg-white sm:bg-[#f1f2f4] flex flex-col items-center sm:justify-center pb-24 sm:pb-0 sm:p-6">
      <div className="w-full sm:max-w-[960px] bg-white sm:rounded-3xl overflow-hidden sm:shadow-xl flex flex-col sm:flex-row">
        <div className="relative overflow-hidden h-[200px] sm:h-auto sm:w-[38%]">
          <img
            src={lamfMobile}
            alt="SIB Digital Loan Against Mutual Funds"
            className="w-full h-full object-cover sm:hidden"
          />
          <img
            src={lamfDesktop}
            alt="SIB Digital Loan Against Mutual Funds"
            className="hidden sm:block w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-6 sm:p-10 flex flex-col">
          <h1 className="text-[21px] sm:text-2xl font-bold text-gray-900">
            Digital Loan Against{' '}
            <span className="text-[#C41230]">Mutual Funds</span>
          </h1>
          <p className="text-gray-500 text-[13px] mt-2">
            Don&apos;t sell your mutual funds, pledge them instead and get
            instant liquidity
          </p>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-8">
            {features.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>

          {/* Mobile: fixed bottom bar so the CTA stays visible while content
              scrolls. Desktop: normal in-flow button within the card. */}
          <div className="fixed inset-x-0 bottom-0 z-20 p-4 bg-white sm:static sm:mt-10 sm:p-0 sm:bg-transparent sm:border-0">
            <button
              type="button"
              onClick={onGetLoan}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#C41230] hover:bg-[#9E0E26] disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 rounded-[10px] transition-colors"
            >
              {loading ? 'Please wait…' : 'Get Loan Against Mutual Funds'}
              {!loading && <RightOutlined className="text-xs" />}
            </button>
          </div>
        </div>
      </div>

      <p className="hidden sm:block text-gray-400 text-[10px] text-center mt-4 px-4 pb-4 sm:pb-0">
        © {new Date().getFullYear()} South Indian Bank Ltd. All rights reserved.
        | DICGC Member Bank | RBI Regulated
      </p>
    </div>
  );
};

export default SibLanding;
